/**
 * Stale-bundle detection — pure, ZERO imports on purpose.
 *
 * Both `@/hooks/use-toast` (the leaf that must never render raw Next.js
 * "Server Action … was not found on the server" text) and
 * `@/lib/utils/stale-bundle-recovery` (which imports `toast`) need this detector.
 * Keeping it import-free is what breaks the use-toast ↔ stale-bundle-recovery cycle.
 *
 * Patterns indicating a stale client bundle after deployment.
 *
 * 'Loading chunk' was REMOVED (2026-05-04) — too broad. It matches ANY webpack
 * chunk load failure including transient network blips, not just post-deploy
 * stale bundles. Use the explicit `ChunkLoadError` name instead.
 */
export const STALE_BUNDLE_PATTERNS = [
  'was not found on the server',
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
] as const;

export function isStaleBundleError(message: string): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return STALE_BUNDLE_PATTERNS.some((p) => lower.includes(p.toLowerCase()));
}

/**
 * The detectable text of an unknown thrown/rendered value: an Error's message,
 * a string as-is, anything else → '' (never stringifies objects/React nodes).
 */
export function staleBundleCandidateText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  // Error-like objects that lost their prototype (structured clone, serialized rejections).
  if (value !== null && typeof value === 'object' && 'message' in value) {
    const { message } = value as { message?: unknown };
    return typeof message === 'string' ? message : '';
  }
  return '';
}

/** True when `value` is an Error or string carrying a stale-bundle signature. */
export function isStaleBundleValue(value: unknown): boolean {
  return isStaleBundleError(staleBundleCandidateText(value));
}

/**
 * The refresh prompt's action. A stale bundle holds dead Server Action ids and chunk hashes;
 * router.refresh() / invalidateQueries() only revalidate data through that same dead bundle,
 * so a full document load is the only fix. Called solely from the user-clicked Refresh button.
 * Lives here (not in use-toast) so the check:react-query reload exemption stays scoped to the
 * stale-bundle module family.
 */
export function reloadToLatestVersion(): void {
  window.location.reload();
}
